import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { VStack } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import PageHeader from 'tio-ui/components/page-header';
import ApplicationConfigReview from 'tio-common/components/tuition-assistance/program-template-review/application-config-review';
import ApproverInstructionsAndFaqs from 'tio-common/components/tuition-assistance/program-template-review/approver-instructions-and-faqs';
import CourseApplicationSetup from 'tio-common/components/tuition-assistance/program-template-review/course-application-setup';
import Notifications from 'tio-common/components/tuition-assistance/program-template-review/notifications';
import ProgramSetupReview from 'tio-common/components/tuition-assistance/program-template-review/program-setup-review';
import ProgramTemplateReadout from 'tio-common/components/tas/program-template-readout/readout';
import type FeaturesService from 'tio-common/services/features';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramTemplateExtension as TasProgramTemplate } from 'tio-data/data/resources/tas-program-template.ext';

interface S {
  Args: {
    model: TasProgramTemplate;
  };
}

export default class AdminProgramTemplatesShow extends Component<S> {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  // Session-local escape hatch back to the legacy review (and back again) —
  // both views share this route, so the switch is view state, not navigation.
  @tracked classicView = false;

  // Mirrors tio-admin's gating of the reimagined builder: flag on → the new
  // read-only template read-out (with a classic-view toggle), flag off → the
  // legacy review stack only.
  get newViewAvailable() {
    return this.features.isEnabled('newTasProgramBuilder');
  }

  get useNewReadout() {
    return this.newViewAvailable && !this.classicView;
  }

  toggleView = () => {
    this.classicView = !this.classicView;
  };

  get title() {
    return (
      String(this.args.model.programName || '').trim() ||
      this.intl.t('program_builder.readout.untitled_program')
    );
  }

  get subtitle() {
    const code = this.args.model.code;
    return code ? this.intl.t('program_builder.readout.program_code', { code }) : undefined;
  }

  <template>
    {{! One PageHeader for every branch — flag state only swaps the body, so
    the route header stays on the standard pattern either way. }}
    <PageHeader @title={{this.title}} @subtitle={{this.subtitle}}>
      <:actions>
        {{#if this.newViewAvailable}}
          {{#if this.classicView}}
            <Button
              @appearance="minimal"
              @size="sm"
              data-test-switch-to-new-view
              {{on "click" this.toggleView}}
            >
              {{t "program_builder.readout.switch_to_new"}}
            </Button>
          {{else}}
            <Button
              @appearance="minimal"
              @size="sm"
              data-test-switch-to-classic-view
              {{on "click" this.toggleView}}
            >
              {{t "program_builder.readout.switch_to_classic"}}
            </Button>
          {{/if}}
        {{/if}}
      </:actions>
    </PageHeader>
    {{#if this.useNewReadout}}
      <ProgramTemplateReadout @model={{@model}} />
    {{else}}
      <VStack>
        <ProgramSetupReview @model={{@model}} />
        <ApplicationConfigReview @model={{@model}} />
        <CourseApplicationSetup @model={{@model}} />
        <ApproverInstructionsAndFaqs @model={{@model}} />
        <Notifications @model={{@model}} />
      </VStack>
    {{/if}}
  </template>
}
